/**
 * LayoutPage — shared page for Docs and Workflows.
 *
 * Renders a sticky sidebar with category navigation and a filtered card grid.
 * Props control which content type is shown and the page title.
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { Search as SearchIcon, Star as StarIcon } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CategoryIcon from '@mui/icons-material/Category';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { Page, Header, Content } from '@backstage/core-components';
import type { Entity } from '@backstage/catalog-model';
import { useLayoutData } from '../../hooks/useLayoutData';
import { LayoutSidebar } from './LayoutSidebar';
import { LayoutCard } from './LayoutCard';
import type { ContentType, LayoutCategory } from '../../types';

/** Convert a category name to a URL-safe slug, e.g. "SRE & DevOps" → "sre-devops" */
const toSlug = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Catalog filters per content type — used to fetch all matching entities
// so uncategorised ones still appear.
const CATALOG_FILTER: Record<ContentType, Record<string, string>> = {
  docs: { kind: 'Component', 'spec.type': 'documentation' },
  workflows: { kind: 'Template' },
};

// ---------------------------------------------------------------------------
// Favourites persistence
// ---------------------------------------------------------------------------

function favouritesKey(contentType: ContentType) {
  return `oneadvanced:favourites:${contentType}:v1`;
}

function loadFavourites(contentType: ContentType): Set<string> {
  try {
    const raw = localStorage.getItem(favouritesKey(contentType));
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set<string>();
  }
}

function saveFavourites(contentType: ContentType, set: Set<string>): void {
  localStorage.setItem(favouritesKey(contentType), JSON.stringify([...set]));
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles()((theme) => ({
  layout: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
  },
  mainContent: { flex: 1, minWidth: 0 },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(2.5),
    flexWrap: 'wrap' as const,
  },
  filterSelect: {
    height: 40,
    minWidth: 140,
    fontSize: 14,
    backgroundColor: theme.palette.background.paper,
    '& .MuiSelect-root': { paddingTop: 8, paddingBottom: 8 },
  },
  searchWrap: {
    flex: 1,
    minWidth: 160,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 1.5),
    backgroundColor: theme.palette.background.paper,
    height: 40,
    boxSizing: 'border-box' as const,
  },
  searchIcon: { color: theme.palette.text.secondary, fontSize: 18, flexShrink: 0 },
  searchInput: { flex: 1, fontSize: 14 },
  section: { marginBottom: theme.spacing(4) },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sectionIconCircle: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sectionTitle: { fontWeight: 700, fontSize: 16, lineHeight: 1.3 },
  sectionSubtitle: { fontSize: 12.5, color: theme.palette.text.secondary, marginTop: 2 },
  centred: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1.5),
  },
  adminBtn: { marginLeft: 'auto' },
}));

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LayoutPageProps {
  contentType: ContentType;
  pageTitle: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LayoutPage({ contentType, pageTitle }: LayoutPageProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const { allowed: isAdmin } = usePermission({
    permission: catalogEntityCreatePermission,
  });

  const { loading, error, categories, itemsByCategory, entityByRef } =
    useLayoutData(contentType);

  // Also fetch ALL matching catalog entities so uncategorised ones are visible
  const [allCatalogEntities, setAllCatalogEntities] = useState<Entity[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    catalogApi
      .getEntities({ filter: CATALOG_FILTER[contentType] as any })
      .then(result => {
        if (!cancelled) {
          setAllCatalogEntities(result.items);
          setCatalogLoading(false);
        }
      })
      .catch(() => { if (!cancelled) setCatalogLoading(false); });
    return () => { cancelled = true; };
  }, [catalogApi, contentType]);

  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') ?? '';
  let selectedId: string;
  if (!categoryParam || categoryParam === 'all') {
    selectedId = 'all';
  } else if (categoryParam === 'favourites') {
    selectedId = 'favourites';
  } else {
    selectedId = categories.find(c => toSlug(c.name) === categoryParam)?.id ?? 'all';
  }
  const setSelectedId = (id: string) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (id === 'all') { next.delete('category'); }
      else if (id === 'favourites') { next.set('category', 'favourites'); }
      else { const cat = categories.find(c => c.id === id); if (cat) { next.set('category', toSlug(cat.name)); } else { next.delete('category'); } }
      return next;
    }, { replace: true });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  const [favourites, setFavourites] = useState<Set<string>>(
    () => loadFavourites(contentType),
  );

  // Subtitle by content type
  const subtitle =
    contentType === 'workflows'
      ? 'Self-service workflows for onboarding, provisioning, and access management.'
      : 'Browse documentation for OneAdvanced products and platform services.';

  // ---------------------------------------------------------------------------
  // Merge: layout DB entity refs + all catalog entities into one map
  // ---------------------------------------------------------------------------

  // Combined entity lookup — DB-fetched refs take priority, then catalog scan
  const mergedEntityByRef = useMemo<Record<string, Entity>>(() => {
    const map: Record<string, Entity> = {};
    // Start with all catalog entities (so uncategorised ones appear)
    for (const entity of allCatalogEntities) {
      const ns = entity.metadata.namespace ?? 'default';
      const ref = `${entity.kind.toLowerCase()}:${ns}/${entity.metadata.name}`;
      map[ref] = entity;
    }
    // Overlay with DB-fetched entities (may have fresher data from getEntitiesByRefs)
    for (const [ref, entity] of Object.entries(entityByRef)) {
      map[ref] = entity;
    }
    return map;
  }, [allCatalogEntities, entityByRef]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  // Map entityRef → category (for card icon/accent)
  const categoryByRef = useMemo(() => {
    const map: Record<string, LayoutCategory> = {};
    for (const cat of categories) {
      const items = itemsByCategory[cat.id] ?? [];
      for (const item of items) {
        if (!map[item.entityRef]) map[item.entityRef] = cat;
      }
    }
    return map;
  }, [categories, itemsByCategory]);

  // Count entities per category (unfiltered, for sidebar badges)
  const countByCategory = useMemo(() => {
    const map: Record<string, number> = {};
    for (const cat of categories) {
      map[cat.id] = (itemsByCategory[cat.id] ?? []).filter(
        item => !!mergedEntityByRef[item.entityRef],
      ).length;
    }
    return map;
  }, [categories, itemsByCategory, mergedEntityByRef]);

  // Total = all resolved entities (categorised + uncategorised)
  const totalCount = Object.keys(mergedEntityByRef).length;

  const favouriteCount = useMemo(
    () =>
      Object.keys(mergedEntityByRef).filter(ref => favourites.has(ref)).length,
    [mergedEntityByRef, favourites],
  );

  // All unique tags across all entities (for dropdown)
  const allTags = useMemo(() => {
    const s = new Set<string>();
    Object.values(mergedEntityByRef).forEach(e =>
      (e.metadata.tags ?? []).forEach(t => s.add(t)),
    );
    return [...s].sort();
  }, [mergedEntityByRef]);

  // Filter predicate
  function matchesSearch(entity: Entity): boolean {
    if (selectedTag !== 'all' && !(entity.metadata.tags ?? []).includes(selectedTag)) return false;
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    const title = (
      entity.metadata.title ?? entity.metadata.name
    ).toLowerCase();
    const desc = (entity.metadata.description ?? '').toLowerCase();
    const tagStr = (entity.metadata.tags ?? []).join(' ').toLowerCase();
    return (
      title.includes(query) || desc.includes(query) || tagStr.includes(query)
    );
  }

  // Visible sections based on selectedId
  const visibleSections = useMemo(() => {
    // All resolved entities from catalog (the source of truth for "All" view)
    const allEntities = Object.values(mergedEntityByRef);

    if (selectedId === 'favourites') {
      const entities = allEntities.filter(
        e => favourites.has(`${e.kind.toLowerCase()}:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`) && matchesSearch(e),
      );
      return entities.length > 0
        ? [{ category: { id: 'favourites', name: 'Favourites', description: 'Your starred items.', contentType, sortOrder: -1 } as LayoutCategory, entities }]
        : [];
    }

    if (selectedId === 'all') {
      // Show every catalog entity in a single flat section
      const entities = allEntities.filter(matchesSearch);
      return entities.length > 0
        ? [{ category: { id: 'all', name: contentType === 'docs' ? 'All Documentation' : 'All Items', contentType, sortOrder: 0 } as LayoutCategory, entities }]
        : [];
    }

    // Specific category — show only the items assigned to it
    const cat = categories.find(c => c.id === selectedId);
    if (!cat) return [];
    const items = itemsByCategory[cat.id] ?? [];
    const entities = items
      .map(item => mergedEntityByRef[item.entityRef])
      .filter((e): e is Entity => !!e && matchesSearch(e));
    return entities.length > 0 ? [{ category: cat, entities }] : [];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mergedEntityByRef,
    categories,
    itemsByCategory,
    selectedId,
    searchQuery,
    selectedTag,
    favourites,
  ]);

  // ---------------------------------------------------------------------------
  // Favourite toggle
  // ---------------------------------------------------------------------------

  function handleToggleFavourite(entityRef: string) {
    setFavourites(prev => {
      const next = new Set(prev);
      if (next.has(entityRef)) next.delete(entityRef);
      else next.add(entityRef);
      saveFavourites(contentType, next);
      return next;
    });
  }

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  function SectionHeader({ category }: { category: LayoutCategory }) {
    const accentColor = category.accentColor ?? '#E6007E';
    const bgColor = `${accentColor}18`;
    return (
      <div className={classes.sectionHeader}>
        <div className={classes.sectionIconCircle} style={{ backgroundColor: bgColor }}>
          {category.id === 'favourites' ? (
            <StarIcon style={{ fontSize: 22, color: '#F59E0B' }} />
          ) : (
            <CategoryIcon style={{ fontSize: 22, color: accentColor }} />
          )}
        </div>
        <div>
          <Typography className={classes.sectionTitle}>{category.name}</Typography>
          {category.description && (
            <Typography className={classes.sectionSubtitle}>
              {category.description}
            </Typography>
          )}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const contentKindLabel = contentType === 'workflows' ? 'workflows' : 'docs';
  let emptyTitleText: string;
  if (searchQuery) {
    emptyTitleText = 'No results found';
  } else if (selectedId === 'favourites') {
    emptyTitleText = 'No favourites yet';
  } else {
    emptyTitleText = `No ${contentKindLabel} in this category`;
  }
  let emptySubtitleText: string;
  if (searchQuery) {
    emptySubtitleText = 'Try a different search term.';
  } else if (selectedId === 'favourites') {
    emptySubtitleText = 'Star a card to add it to your favourites.';
  } else if (isAdmin) {
    emptySubtitleText = 'Use the admin panel to add items to this category.';
  } else {
    emptySubtitleText = 'Items will appear here once they are added to this category.';
  }

  return (
    <Page themeId="tool">
      <Header title={pageTitle} subtitle={subtitle}>
        {isAdmin && (
          <>
            <IconButton
              color="inherit"
              aria-label="more options"
              onClick={e => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { setMenuAnchor(null); navigate(`/oneadvanced-admin/layouts/${contentType}`); }}>
                Manage layout
              </MenuItem>
            </Menu>
          </>
        )}
      </Header>
      <Content>
        {/* eslint-disable no-nested-ternary */}
        {(loading || catalogLoading) ? (
          <Box className={classes.centred}>
            <CircularProgress />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : error ? (
          <Box className={classes.centred}>
            <Typography variant="body1" style={{ fontWeight: 700 }}>
              Failed to load
            </Typography>
            <Typography variant="body2">{error.message}</Typography>
          </Box>
        ) : (
          <div className={classes.layout}>
            {/* Sidebar */}
            <LayoutSidebar
              categories={categories}
              countByCategory={countByCategory}
              totalCount={totalCount}
              favouriteCount={favouriteCount}
              selectedId={selectedId}
              contentTypeLabel={
                contentType === 'workflows' ? 'Workflows' : 'Docs'
              }
              onSelect={setSelectedId}
            />

            {/* Main content */}
            <div className={classes.mainContent}>
              {/* Search bar */}
              <div className={classes.toolbar}>
                <div className={classes.searchWrap}>
                  <SearchIcon className={classes.searchIcon} />
                  <InputBase
                    className={classes.searchInput}
                    placeholder={`Search ${contentType === 'workflows' ? 'workflows' : 'docs'}…`}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    inputProps={{ 'aria-label': `search ${contentType}` }}
                  />
                  {searchQuery && (
                    <IconButton
                      size="small"
                      style={{ padding: 2 }}
                      onClick={() => setSearchQuery('')}
                    >
                      <ClearIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </div>
                {allTags.length > 0 && (
                  <Select
                    variant="outlined"
                    className={classes.filterSelect}
                    value={selectedTag}
                    onChange={e => setSelectedTag(e.target.value as string)}
                    displayEmpty
                  >
                    <MenuItem value="all">All tags</MenuItem>
                    {allTags.map(tag => (
                      <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                    ))}
                  </Select>
                )}
              </div>

              {/* Sections */}
              {visibleSections.length === 0 ? (
                <Box className={classes.centred}>
                  <CategoryIcon style={{ fontSize: 48 }} />
                  <Typography variant="body1" style={{ fontWeight: 700 }}>
                    {emptyTitleText}
                  </Typography>
                  <Typography variant="body2">
                    {emptySubtitleText}
                  </Typography>
                </Box>
              ) : (
                visibleSections.map(({ category, entities }) => (
                  <div key={category.id} className={classes.section}>
                    <SectionHeader category={category} />
                    <Grid container spacing={2}>
                      {entities.map(entity => {
                        const entityRef = `${entity.kind.toLowerCase()}:${
                          entity.metadata.namespace ?? 'default'
                        }/${entity.metadata.name}`;
                        return (
                          <Grid
                            key={entityRef}
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                          >
                            <LayoutCard
                              entity={entity}
                              contentType={contentType}
                              category={categoryByRef[entityRef]}
                              isFavourite={favourites.has(entityRef)}
                              onToggleFavourite={handleToggleFavourite}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        {/* eslint-enable no-nested-ternary */}
      </Content>
    </Page>
  );
}
